import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { claimAdRecovery } from '../apiClient';
import { BucksBadge } from './BucksDisplay';
import EloBadge from './EloBadge';
import NotificationBell from './NotificationBell';
import AccessibilityPanel from './AccessibilityPanel';
import RankPromotionModal from './RankPromotionModal';
import Footer from './Footer';

const hardwoodFloorStyle = {
  background: `
    repeating-linear-gradient(90deg, #3d2817 0px, #4a3423 2px, #5c4033 4px, #4a3423 6px, #3d2817 8px),
    repeating-linear-gradient(0deg, transparent 0px, transparent 60px, rgba(0,0,0,0.15) 60px, rgba(0,0,0,0.15) 62px),
    linear-gradient(180deg, #4a3423 0%, #3d2817 50%, #2d1f12 100%)
  `,
  backgroundBlendMode: "overlay, overlay, normal"
};

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export default function Layout() {
  const { user, isAuthenticated, token, updateUser, signout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showA11y, setShowA11y] = useState(false);
  const [adRecoveryMsg, setAdRecoveryMsg] = useState('');
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try {
      const d = localStorage.getItem('club_nudge_dismissed');
      return d ? Date.now() - parseInt(d) < 7 * 86400000 : false; // 7-day TTL
    } catch { return false; }
  });
  const [rankPromotion, setRankPromotion] = useState(null);

  // Full-screen pages handle their own layout entirely
  const fullScreenPaths = ['/', '/play', '/signin', '/home', '/onboarding', '/euchre', '/community'];
  const isFullScreen = fullScreenPaths.includes(location.pathname);

  if (isFullScreen) {
    return <Outlet />;
  }

  // Show back button on all sub-pages (not landing/home)
  const showBack = location.pathname !== '/home';

  const pageContent = (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 32px',
      maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 1
    }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: isMobile ? 16 : 24 }}>
        <Link to="/home" style={{ textDecoration: 'none' }}>
          <img
            src="/BuckEuchreLogoWhite.png"
            alt="Euchre Table"
            style={{
              width: isMobile ? 320 : 500,
              maxWidth: '90vw',
              height: 'auto',
              filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.5))'
            }}
          />
        </Link>
      </div>

      {/* User info bar (below logo, matching home screen style) */}
      {isAuthenticated && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: isMobile ? 16 : 24,
          background: 'rgba(0,0,0,0.3)', borderRadius: 8,
          padding: isMobile ? '10px 14px' : '12px 20px',
          flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 8 : 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexWrap: 'wrap' }}>
            {showBack && (
              <button onClick={() => navigate('/home')} style={{
                background: 'transparent', border: '1px solid #30363d', color: '#8b949e',
                padding: isMobile ? '4px 10px' : '5px 14px', borderRadius: 6, cursor: 'pointer',
                fontSize: isMobile ? 12 : 13
              }}>Back</button>
            )}
            <span style={{ color: '#c9d1d9', fontSize: isMobile ? 13 : 14, fontWeight: 500 }}>
              {user?.nickname || 'Player'}
            </span>
            <BucksBadge amount={user?.playMoneyBalance || 0} />
            <EloBadge cardRankId={user?.cardRankId || user?.permanentCardRank} suitSymbol={user?.suitSymbol || user?.preferredSuit || '♣'} points={user?.activeRP ?? user?.rankPoints ?? 0} rankName={user?.cardRankName || user?.rankName} showPoints={false} size="sm" badgeLabel="Your Rank" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10 }}>
            <NotificationBell />
            <button onClick={() => setShowA11y(true)} style={{
              background: 'transparent', border: 'none', color: '#8b949e',
              fontSize: 16, cursor: 'pointer', padding: 4
            }} title="Accessibility">&#9881;</button>
            <button onClick={() => { signout(); navigate('/'); }} style={{
              background: 'transparent', border: '1px solid #30363d', color: '#8b949e',
              padding: isMobile ? '5px 10px' : '6px 14px', borderRadius: 6, cursor: 'pointer',
              fontSize: isMobile ? 12 : 13
            }}>
              Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Not authenticated — just show sign in link */}
      {!isAuthenticated && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: isMobile ? 16 : 24,
          background: 'rgba(0,0,0,0.3)', borderRadius: 8,
          padding: isMobile ? '10px 14px' : '12px 20px'
        }}>
          {showBack && (
            <button onClick={() => navigate(-1)} style={{
              background: 'transparent', border: '1px solid #30363d', color: '#8b949e',
              padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13
            }}>Back</button>
          )}
          <div style={{ flex: 1 }} />
          <Link to="/signin" style={{ color: '#58a6ff', textDecoration: 'none', fontSize: 14 }}>
            Sign In
          </Link>
        </div>
      )}

      {/* Ad Recovery Banner (low balance) — hidden for Club Members */}
      {isAuthenticated && !user?.membershipTier?.includes('club') && (user?.playMoneyBalance || 0) < 10 && (
        <div style={{
          background: '#1a1f2e', border: '1px solid #fbbf2440', borderRadius: 8,
          padding: '10px 16px', marginBottom: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 8
        }}>
          <div style={{ fontSize: 13, color: '#fbbf24' }}>
            Low on Bucks? Claim 15 free Bucks (up to 3x/day)
          </div>
          {adRecoveryMsg ? (
            <span style={{ fontSize: 12, color: adRecoveryMsg.includes('error') ? '#f85149' : '#3fb950' }}>{adRecoveryMsg}</span>
          ) : (
            <button onClick={async () => {
              const result = await claimAdRecovery(token);
              if (result.error) {
                setAdRecoveryMsg(result.error);
              } else {
                setAdRecoveryMsg(`+${result.amount} Bucks claimed!`);
                if (result.newBalance != null) updateUser({ playMoneyBalance: result.newBalance });
              }
              setTimeout(() => setAdRecoveryMsg(''), 4000);
            }} style={{
              background: '#238636', color: '#fff', border: 'none', borderRadius: 6,
              padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600
            }}>
              Claim Bucks
            </button>
          )}
        </div>
      )}

      {/* Club Membership Nudge */}
      {isAuthenticated && (user?.membershipTier || 'free') === 'free' && !nudgeDismissed && (
        <div style={{
          background: 'linear-gradient(135deg, #1c3d2a20, #0d111720)',
          border: '1px solid #3fb95030', borderRadius: 8,
          padding: '8px 16px', marginBottom: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8
        }}>
          <a href="/membership" style={{ fontSize: 12, color: '#3fb950', textDecoration: 'none', fontWeight: 600 }}>
            Join the Club for ad-free play, exclusive cosmetics & more
          </a>
          <button onClick={() => {
            setNudgeDismissed(true);
            try { localStorage.setItem('club_nudge_dismissed', String(Date.now())); } catch {}
          }} style={{
            background: 'transparent', border: 'none', color: '#484f58',
            cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1
          }}>{'\u00d7'}</button>
        </div>
      )}

      {/* Page Content */}
      <Outlet />

      {/* Footer */}
      <Footer isMobile={isMobile} />
    </div>
  );

  // Mobile: fixed outer + scrollable inner
  if (isMobile) {
    return (
      <div style={{ ...hardwoodFloorStyle, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, color: '#e2e8f0' }}>
        <div style={{ height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {pageContent}
        </div>
        {showA11y && <AccessibilityPanel onClose={() => setShowA11y(false)} />}
        {rankPromotion && <RankPromotionModal promotion={rankPromotion} onClose={() => setRankPromotion(null)} />}
      </div>
    );
  }

  // Desktop: normal scrollable page
  return (
    <div style={{ ...hardwoodFloorStyle, minHeight: '100vh', position: 'relative', color: '#e2e8f0' }}>
      <img src="/BuckEuchreLogo.png" alt="" style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        width: 500, height: "auto", opacity: 0.08, pointerEvents: "none", zIndex: 0
      }} />
      {pageContent}
      {showA11y && <AccessibilityPanel onClose={() => setShowA11y(false)} />}
      {rankPromotion && <RankPromotionModal promotion={rankPromotion} onClose={() => setRankPromotion(null)} />}
    </div>
  );
}
